import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';

export default function TablePagination({
  currentPage,
  setCurrentPage,
  rowsPerPage,
  setRowsPerPage,
  totalItems,
  pageSizeOptions = [10, 25, 50, 100]
}) {
  const { isDarkMode } = useTheme();

  const totalPages = Math.ceil(totalItems / rowsPerPage) || 1;
  const startItem = totalItems > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0;
  const endItem = Math.min(currentPage * rowsPerPage, totalItems);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, start + maxVisible - 1);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <div
      style={{
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '12px',
        borderTop: `1px solid ${isDarkMode ? '#1E2433' : '#E5E7EB'}`,
        fontSize: '12px',
        color: isDarkMode ? '#9CA3AF' : '#6B7280',
        backgroundColor: isDarkMode ? '#111622' : '#FFFFFF',
      }}
    >
      {/* Left: Rows selector + Item bounds info */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span>Rows per page:</span>
          <select
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            style={{
              padding: '4px 8px',
              borderRadius: '6px',
              backgroundColor: isDarkMode ? '#161B26' : '#F9FAFB',
              border: `1px solid ${isDarkMode ? '#252D3D' : '#D1D5DB'}`,
              color: isDarkMode ? '#F3F4F6' : '#111827',
              fontSize: '12px',
              outline: 'none',
              cursor: 'pointer',
            }}
          >
            {pageSizeOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>
        <span>
          Showing {startItem} to {endItem} of {totalItems} items
        </span>
      </div>

      {/* Right: Page buttons */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <button
          onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          disabled={currentPage === 1 || totalItems === 0}
          style={{
            border: `1px solid ${isDarkMode ? '#252D3D' : '#E5E7EB'}`,
            backgroundColor: isDarkMode ? '#161B26' : '#FFFFFF',
            color: isDarkMode ? '#F3F4F6' : '#111827',
            borderRadius: '6px',
            padding: '4px 10px',
            cursor: currentPage === 1 || totalItems === 0 ? 'not-allowed' : 'pointer',
            opacity: currentPage === 1 || totalItems === 0 ? 0.5 : 1,
          }}
        >
          &lt;
        </button>

        {getPageNumbers().map((pg) => (
          <button
            key={pg}
            onClick={() => setCurrentPage(pg)}
            style={{
              border: `1px solid ${currentPage === pg ? '#0082F8' : isDarkMode ? '#252D3D' : '#E5E7EB'}`,
              backgroundColor: currentPage === pg ? '#0082F8' : isDarkMode ? '#161B26' : '#FFFFFF',
              color: currentPage === pg ? '#FFFFFF' : isDarkMode ? '#9CA3AF' : '#6B7280',
              borderRadius: '6px',
              padding: '4px 10px',
              fontWeight: currentPage === pg ? 600 : 400,
              cursor: 'pointer',
            }}
          >
            {pg}
          </button>
        ))}

        <button
          onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          disabled={currentPage === totalPages || totalItems === 0}
          style={{
            border: `1px solid ${isDarkMode ? '#252D3D' : '#E5E7EB'}`,
            backgroundColor: isDarkMode ? '#161B26' : '#FFFFFF',
            color: isDarkMode ? '#F3F4F6' : '#111827',
            borderRadius: '6px',
            padding: '4px 10px',
            cursor: currentPage === totalPages || totalItems === 0 ? 'not-allowed' : 'pointer',
            opacity: currentPage === totalPages || totalItems === 0 ? 0.5 : 1,
          }}
        >
          &gt;
        </button>
      </div>
    </div>
  );
}
